/**
 * Trial Activation Success Page
 * Shown after a clinic successfully starts their free trial.
 * Fully localized via provider.trialActivation.* namespace.
 */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useClinicData } from '../../context/ClinicDataContext';
import { usePackageCatalog, PackageCatalogProvider } from '../../context/PackageCatalogContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import {
  Sparkles, Clock, Users, FileText, TrendingUp,
  ArrowRight, Gift, Rocket, Bell,
  BarChart3, UserPlus, Loader2
} from 'lucide-react';
import { Progress } from '../../components/ui/progress';

const TrialActivationSuccessContent = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { clinicUser, createAndLoginNewClinic } = useClinicData();
  const { getPackageById, refreshCatalog } = usePackageCatalog();
  const [progress, setProgress] = useState(0);
  const [isInitializing, setIsInitializing] = useState(true);

  useEffect(() => {
    refreshCatalog();
  }, [refreshCatalog]);

  const trialPackage = getPackageById('TRIAL');
  const trialDays = trialPackage?.duration?.defaultDays || 14;
  const trialLeads = trialPackage?.limits?.leads || 5;

  useEffect(() => {
    if (!clinicUser) {
      createAndLoginNewClinic({
        email: 'new.clinic@treatx.demo',
        clinicName: 'My New Clinic',
      });
    }
    const timer = setTimeout(() => {
      setIsInitializing(false);
    }, 300);
    return () => clearTimeout(timer);
  }, [clinicUser, createAndLoginNewClinic]);

  useEffect(() => {
    if (!isInitializing) {
      const timer = setInterval(() => {
        setProgress(prev => {
          if (prev >= 100) {
            clearInterval(timer);
            return 100;
          }
          return prev + 2;
        });
      }, 50);
      return () => clearInterval(timer);
    }
  }, [isInitializing]);

  const trialEndDate = new Date();
  trialEndDate.setDate(trialEndDate.getDate() + trialDays);

  const handleNavigate = (path) => {
    navigate(path, { replace: false });
  };

  const quickStartSteps = [
    {
      icon: UserPlus,
      titleKey: 'provider.trialActivation.qsAddTeamTitle',
      descKey: 'provider.trialActivation.qsAddTeamDesc',
      actionKey: 'provider.trialActivation.qsAddTeamAction',
      path: '/clinic/admin/users',
    },
    {
      icon: FileText,
      titleKey: 'provider.trialActivation.qsProfileTitle',
      descKey: 'provider.trialActivation.qsProfileDesc',
      actionKey: 'provider.trialActivation.qsProfileAction',
      path: '/clinic/admin',
    },
    {
      icon: BarChart3,
      titleKey: 'provider.trialActivation.qsDashboardTitle',
      descKey: 'provider.trialActivation.qsDashboardDesc',
      actionKey: 'provider.trialActivation.qsDashboardAction',
      path: '/clinic/admin',
    },
  ];

  const trialFeatures = [
    { icon: Clock, text: t('provider.trialActivation.featureDaysLabel', { days: trialDays }), value: t('provider.trialActivation.featureActive') },
    { icon: Users, text: t('provider.trialActivation.featureLeadLimit'), value: t('provider.trialActivation.featureLeadsMonth', { n: trialLeads }) },
    { icon: FileText, text: t('provider.trialActivation.featureLeadMgmt'), value: t('provider.trialActivation.featureIncluded') },
    { icon: TrendingUp, text: t('provider.trialActivation.featureAnalytics'), value: t('provider.trialActivation.featureBasic') },
  ];

  if (isInitializing) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-green-50 via-emerald-50 to-teal-50">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin text-green-600 mx-auto mb-4" />
          <p className="text-gray-600">{t('provider.trialActivation.settingUp')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 via-emerald-50 to-teal-50" data-testid="trial-success-page">
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 right-0 w-96 h-96 bg-gradient-to-br from-green-200/40 to-emerald-200/40 rounded-full blur-3xl transform translate-x-1/2 -translate-y-1/2" />
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-gradient-to-tr from-teal-200/40 to-cyan-200/40 rounded-full blur-3xl transform -translate-x-1/2 translate-y-1/2" />
      </div>

      <div className="relative container mx-auto px-4 py-12 max-w-4xl">
        {/* Success Header */}
        <div className="text-center mb-10">
          <div className="inline-flex items-center justify-center w-24 h-24 rounded-full bg-gradient-to-br from-green-400 to-emerald-500 shadow-xl mb-6 animate-bounce">
            <Rocket className="h-12 w-12 text-white" />
          </div>
          <Badge className="bg-green-100 text-green-700 border-green-300 mb-4 px-4 py-1">
            <Sparkles className="h-3 w-3 mr-1" />
            {t('provider.trialActivation.badge')}
          </Badge>
          <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-3">
            {t('provider.trialActivation.heroTitle')}
          </h1>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            {t('provider.trialActivation.heroSubtitle', { days: trialDays })}
          </p>
        </div>

        {/* Trial Status Card */}
        <Card className="mb-8 border-2 border-green-200 shadow-xl overflow-hidden">
          <div className="bg-gradient-to-r from-green-500 to-emerald-500 text-white p-6">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-xl font-bold">{t('provider.trialActivation.statusTitle')}</h2>
                <p className="text-green-100">{t('provider.trialActivation.statusReady')}</p>
              </div>
              <div className="text-right">
                <p className="text-sm text-green-100">{t('provider.trialActivation.trialEnds')}</p>
                <p className="text-lg font-bold">{trialEndDate.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}</p>
              </div>
            </div>
          </div>
          <CardContent className="p-6">
            <div className="mb-4">
              <div className="flex justify-between text-sm mb-2">
                <span className="text-gray-600">{t('provider.trialActivation.trialProgress')}</span>
                <span className="text-green-600 font-medium">{t('provider.trialActivation.dayOf', { days: trialDays })}</span>
              </div>
              <Progress value={Math.round(100 / trialDays)} className="h-2" />
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {trialFeatures.map((feature, idx) => (
                <div key={idx} className="bg-gray-50 rounded-lg p-3 text-center">
                  <feature.icon className="h-5 w-5 text-green-600 mx-auto mb-1" />
                  <p className="text-xs text-gray-500">{feature.text}</p>
                  <p className="text-sm font-semibold text-gray-900">{feature.value}</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Quick Start Guide */}
        <Card className="mb-8 shadow-lg">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Gift className="h-5 w-5 text-green-600" />
              {t('provider.trialActivation.quickStartTitle')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-3 gap-4">
              {quickStartSteps.map((step, idx) => (
                <div
                  key={idx}
                  className="border rounded-xl p-4 hover:border-green-300 hover:bg-green-50/50 transition-all cursor-pointer group"
                  onClick={() => handleNavigate(step.path)}
                >
                  <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center mb-3 group-hover:bg-green-200 transition-colors">
                    <step.icon className="h-5 w-5 text-green-600" />
                  </div>
                  <h3 className="font-semibold text-gray-900 mb-1">{t(step.titleKey)}</h3>
                  <p className="text-sm text-gray-500 mb-3">{t(step.descKey)}</p>
                  <span className="text-sm text-green-600 font-medium flex items-center gap-1 group-hover:gap-2 transition-all">
                    {t(step.actionKey)}
                    <ArrowRight className="h-4 w-4" />
                  </span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* What Happens Next */}
        <Card className="mb-8 bg-gradient-to-br from-blue-50 to-indigo-50 border-blue-200">
          <CardContent className="p-6">
            <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <Bell className="h-5 w-5 text-blue-600" />
              {t('provider.trialActivation.whatNextTitle')}
            </h3>
            <div className="space-y-3">
              <div className="flex items-start gap-3">
                <div className="w-6 h-6 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <span className="text-xs font-bold text-blue-600">1</span>
                </div>
                <div>
                  <p className="font-medium text-gray-900">{t('provider.trialActivation.step1Title')}</p>
                  <p className="text-sm text-gray-600">{t('provider.trialActivation.step1Desc')}</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-6 h-6 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <span className="text-xs font-bold text-blue-600">2</span>
                </div>
                <div>
                  <p className="font-medium text-gray-900">{t('provider.trialActivation.step2Title')}</p>
                  <p className="text-sm text-gray-600">{t('provider.trialActivation.step2Desc', { n: trialLeads })}</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-6 h-6 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <span className="text-xs font-bold text-blue-600">3</span>
                </div>
                <div>
                  <p className="font-medium text-gray-900">{t('provider.trialActivation.step3Title')}</p>
                  <p className="text-sm text-gray-600">{t('provider.trialActivation.step3Desc')}</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* CTA Buttons */}
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <Button
            size="lg"
            className="bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 shadow-lg"
            onClick={() => handleNavigate('/clinic/admin')}
            data-testid="go-to-dashboard-btn"
          >
            {t('provider.trialActivation.goToDashboard')}
            <ArrowRight className="h-5 w-5 ml-2" />
          </Button>
          <Button
            size="lg"
            variant="outline"
            className="border-green-300 text-green-700 hover:bg-green-50"
            onClick={() => handleNavigate('/clinic/packages')}
          >
            {t('provider.trialActivation.viewUpgrade')}
          </Button>
        </div>

        {/* Support Footer */}
        <div className="text-center mt-10">
          <p className="text-gray-600">
            {t('provider.trialActivation.supportPrefix')}{' '}
            <a href="#/contact" className="text-green-600 hover:underline font-medium">
              {t('provider.trialActivation.supportLink')}
            </a>
          </p>
        </div>
      </div>
    </div>
  );
};

const TrialActivationSuccessPage = () => {
  return (
    <PackageCatalogProvider>
      <TrialActivationSuccessContent />
    </PackageCatalogProvider>
  );
};

export default TrialActivationSuccessPage;
